<template>
  <div class="kb-page">
    <AppHeader />

    <!-- Hero -->
    <section class="kb-products-hero">
      <div class="kb-products-hero__overlay"></div>
      <div class="container-xl kb-products-hero__content" data-aos="fade-up">
        <div class="kb-page-hero__breadcrumb">
          <router-link to="/">Home</router-link>
          <i class="bi bi-chevron-right mx-2"></i>
          <span>Products</span>
        </div>
        <h1>Our Products</h1>
        <p>Premium quality tarpaulins, agro nets, and protection solutions — manufactured in Mumbai, trusted across India</p>
      </div>
    </section>

    <!-- Filter Tabs -->
    <section class="kb-filter-bar">
      <div class="container-xl">
        <div class="kb-filters">
          <button
            v-for="cat in categories"
            :key="cat"
            class="kb-filter-btn"
            :class="{ active: activeCategory === cat }"
            @click="activeCategory = cat"
          >{{ cat }}</button>
        </div>
      </div>
    </section>

    <!-- Products Grid -->
    <section class="kb-section kb-section--light">
      <div class="container-xl">
        <div class="kb-all-products-grid">
          <div
            v-for="(product, idx) in filteredProducts"
            :key="product.id"
            class="kb-product-full-card"
            data-aos="fade-up"
            :data-aos-delay="(idx % 3) * 80"
            style="cursor:pointer"
            @click="$router.push(`/products/${product.slug}`)"
          >
            <div class="kb-product-full-card__img">
              <img :src="product.heroImage" :alt="product.name" loading="lazy" />
              <span class="kb-product-full-card__badge" :style="{ background: product.badgeColor }">{{ product.badge }}</span>
              <span class="kb-product-full-card__cat">{{ product.category }}</span>
            </div>
            <div class="kb-product-full-card__body">
              <h3>{{ product.name }}</h3>
              <p class="kb-product-full-card__tagline">{{ product.tagline }}</p>
              <p class="kb-product-full-card__desc">{{ product.shortDesc }}</p>
              <div class="kb-product-full-card__features">
                <div v-for="f in product.features.slice(0,3)" :key="f.title" class="kb-product-full-card__feature">
                  <i :class="f.icon" :style="{ color: f.color }"></i>
                  <span>{{ f.title }}</span>
                </div>
              </div>
              <div class="kb-product-full-card__actions">
                <router-link :to="`/products/${product.slug}`" class="kb-btn kb-btn--navy kb-btn--sm">
                  Full Details <i class="bi bi-arrow-right ms-1"></i>
                </router-link>
                <a
                  v-if="product.brochurePdf"
                  :href="product.brochurePdf"
                  download
                  class="kb-btn kb-btn--outline-dark kb-btn--sm"
                  @click.stop
                >
                  <i class="bi bi-download me-1"></i> Brochure
                </a>
                <a
                  v-if="product.pricePdf"
                  :href="product.pricePdf"
                  download
                  class="kb-btn kb-btn--outline-dark kb-btn--sm"
                  @click.stop
                >
                  <i class="bi bi-download me-1"></i> Price List
                </a>
                <a
                  v-else
                  :href="`https://wa.me/919892310123?text=Hello%2C%20I%20would%20like%20to%20enquire%20about%3A%20${encodeURIComponent(product.name)}`"
                  target="_blank"
                  class="kb-btn kb-btn--outline-dark kb-btn--sm"
                  @click.stop
                >
                  <i class="bi bi-whatsapp me-1"></i> Enquire
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="kb-cta-banner">
      <div class="container-xl">
        <div class="kb-cta-banner__content" data-aos="fade-up">
          <h2>Need Custom Sizes or Bulk Orders?</h2>
          <p>We manufacture to your exact specifications. Get a quote for any quantity.</p>
          <div class="kb-cta-banner__actions">
            <a href="https://wa.me/919892310123?text=Hello%2C%20I%20need%20a%20custom%20order%20quote" target="_blank" class="kb-btn kb-btn--primary">
              <i class="bi bi-whatsapp me-2"></i>Request Custom Quote
            </a>
          </div>
        </div>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { setSeo } from '@/utils/seo.js'
import { products } from '@/data/products.js'

export default {
  name: 'ProductsPage',
  components: { AppHeader, AppFooter },
  mounted() {
    setSeo({
      title: 'All Products – HDPE Tarpaulins, Agro Nets, Nylon Ropes | KeBee Industries',
      description: 'Browse all KeBee Industries products: HDPE Tarpaulins, PVC Fire-Retardant Tarpaulins, Canvas Tarpaulins, Agro Shade Nets, Barsati Plastic & Nylon Ropes. Bulk orders, custom sizes, pan-India delivery from Mumbai.',
      path: '/products',
      keywords: 'HDPE tarpaulin, PVC FR tarpaulin, canvas tarpaulin, agro shade net, barsati plastic, nylon rope, tarpaulin price list Mumbai'
    })
  },
  data() {
    return {
      products,
      activeCategory: 'All',
    }
  },
  computed: {
    categories() {
      const cats = ['All', ...new Set(this.products.map(p => p.category))]
      return cats
    },
    filteredProducts() {
      if (this.activeCategory === 'All') return this.products
      return this.products.filter(p => p.category === this.activeCategory)
    }
  }
}
</script>

<style scoped>
/* Hero */
.kb-products-hero {
  min-height: 380px;
  background: linear-gradient(135deg, #0F1F3D 0%, #1E3A6E 100%);
  position: relative;
  display: flex;
  align-items: flex-end;
  padding-bottom: 60px;
}
.kb-products-hero__overlay {
  position: absolute;
  inset: 0;
  background: url('@/assets/slider1.webp') center/cover no-repeat;
  opacity: 0.15;
}
.kb-products-hero__content {
  position: relative;
  z-index: 2;
  padding-top: 120px;
}
.kb-page-hero__breadcrumb {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: rgba(255,255,255,0.6);
  margin-bottom: 1rem;
}
.kb-page-hero__breadcrumb a {
  color: rgba(255,255,255,0.6);
  text-decoration: none;
}
.kb-page-hero__breadcrumb a:hover { color: #F59E0B; }
.kb-products-hero__content h1 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 0.75rem;
}
.kb-products-hero__content p {
  color: rgba(255,255,255,0.72);
  font-size: 1.05rem;
}

/* Filter Bar */
.kb-filter-bar {
  background: #fff;
  border-bottom: 1px solid #E5E7EB;
  position: sticky;
  top: 68px;
  z-index: 50;
}
.kb-filters {
  display: flex;
  gap: 0.5rem;
  padding: 14px 0;
  overflow-x: auto;
}
.kb-filter-btn {
  flex-shrink: 0;
  padding: 8px 20px;
  border-radius: 50px;
  border: 1.5px solid #E5E7EB;
  background: transparent;
  color: #6B7280;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}
.kb-filter-btn:hover { border-color: #0F1F3D; color: #0F1F3D; }
.kb-filter-btn.active { background: #0F1F3D; border-color: #0F1F3D; color: #fff; }

/* Products Grid */
.kb-all-products-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}
.kb-product-full-card {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(15,31,61,0.07);
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;
}
.kb-product-full-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 14px 44px rgba(15,31,61,0.13);
}
.kb-product-full-card__img {
  position: relative;
  height: 240px;
  overflow: hidden;
}
.kb-product-full-card__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s;
}
.kb-product-full-card:hover .kb-product-full-card__img img { transform: scale(1.05); }
.kb-product-full-card__badge {
  position: absolute;
  top: 14px;
  right: 14px;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.kb-product-full-card__cat {
  position: absolute;
  bottom: 14px;
  left: 14px;
  background: rgba(15,31,61,0.7);
  color: rgba(255,255,255,0.9);
  font-size: 0.7rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 50px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.kb-product-full-card__body {
  padding: 1.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.kb-product-full-card__body h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 0.25rem;
}
.kb-product-full-card__tagline {
  font-style: italic;
  color: #F59E0B;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}
.kb-product-full-card__desc {
  font-size: 0.875rem;
  color: #6B7280;
  line-height: 1.6;
  margin-bottom: 1.25rem;
  flex: 1;
}
.kb-product-full-card__features {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}
.kb-product-full-card__feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.82rem;
  color: #374151;
  font-weight: 500;
}
.kb-product-full-card__feature i {
  font-size: 0.85rem;
}
.kb-product-full-card__actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* CTA Banner */
.kb-cta-banner {
  background: linear-gradient(135deg, #0F1F3D 0%, #1E3A6E 100%);
  padding: 80px 0;
  text-align: center;
}
.kb-cta-banner__content h2 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
}
.kb-cta-banner__content p {
  color: rgba(255,255,255,0.72);
  font-size: 1.05rem;
  margin-bottom: 2rem;
}
.kb-cta-banner__actions { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }

@media (max-width: 992px) { .kb-all-products-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 576px) { .kb-all-products-grid { grid-template-columns: 1fr; } }
</style>
